import React, { createContext, FC, useMemo } from 'react';

import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { useContextWithValidation } from '@adsk/alloy-react-context';

import { FEATURE_FLAGS } from '../../constants';
import { HQAccount } from '../../models/Account';
import {
  filterAccountsWithoutCreation,
  filterNonAccAccountAdminAccounts,
  filterNonExecutiveAccounts,
  filterAccAccountsByAllowlist,
} from '../../utils';

type AccessLevelContext = {
  allAccounts: HQAccount[];
  accountsLoading: boolean;
  accountsError: HttpError;
  accountsWithProjectCreation: HQAccount[];
  accountsUserIsAccAccountAdmin: HQAccount[];
  accountsUserIsExecutive: HQAccount[];
  accountsWithCapitalPlanning: HQAccount[];
};

const accessLevelContextDefaultValue = {
  allAccounts: [],
  accountsLoading: true,
  accountsError: undefined,
  accountsWithProjectCreation: [],
  accountsUserIsAccAccountAdmin: [],
  accountsUserIsExecutive: [],
  accountsWithCapitalPlanning: [],
};
const AccessLevelContext = createContext<AccessLevelContext>(
  accessLevelContextDefaultValue,
);

export const useAccessLevelContext = () =>
  useContextWithValidation(AccessLevelContext, {
    hook: 'useAccessLevelContext',
    provider: 'AccessLevelContextProvider',
  });

type AccessLevelContextProps = {
  accounts: HQAccount[];
  accountsLoading: boolean;
  accountsError?: HttpError;
};

const emptyArray: string[] = [];
export const AccessLevelContextProvider: FC<AccessLevelContextProps> = ({
  accounts,
  accountsLoading,
  accountsError,
  children,
}) => {
  const capitalPlanningAccountIds: string[] = useAlloyFeatureFlag(
    FEATURE_FLAGS.AllowListCapitalPlanning,
    emptyArray,
  ).value;
  const accountsWithCapitalPlanning = useMemo(
    () => filterAccAccountsByAllowlist(accounts, capitalPlanningAccountIds),
    [accounts, capitalPlanningAccountIds],
  );

  const accountsWithProjectCreation = useMemo(
    () => filterAccountsWithoutCreation(accounts),
    [accounts],
  );
  const accountsUserIsAccAccountAdmin = useMemo(
    () => filterNonAccAccountAdminAccounts(accounts),
    [accounts],
  );
  const accountsUserIsExecutive = useMemo(
    () => filterNonExecutiveAccounts(accounts),
    [accounts],
  );

  return (
    <AccessLevelContext.Provider
      value={{
        allAccounts: accounts,
        accountsLoading,
        accountsError,
        accountsWithProjectCreation,
        accountsUserIsAccAccountAdmin,
        accountsUserIsExecutive,
        accountsWithCapitalPlanning,
      }}
    >
      {children}
    </AccessLevelContext.Provider>
  );
};
